import React, { useRef } from 'react';
import { StanButton, StanCheckbox, StanModal } from '@/components';
import {
    StripePaymentInput,
    StripePaymentInputRef,
} from '@/components/third-party/StripePaymentInput/StripePaymentInput';
import { DuplicatePaymentModal } from '@/features/subscriptions/components/DuplicatePaymentModal';
import { useSessionCountry } from '@/features/auth/hooks/useSessionCountry';
import { getGDPRComplianceDocument } from '@/features/auth/utils/gdpr';
import { useStartTrialModal } from '../../hooks/useStartTrialModal';
import { LEGAL_URLS } from '@/lib/constants';

interface StartTrialModalProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Modal for affiliate users to start their 14-day free trial
 * Matches Vue's StartTrialModal.vue behavior
 */
export const StartTrialModal: React.FC<StartTrialModalProps> = ({ isOpen, onClose }) => {
    const { country } = useSessionCountry();
    const stripeRef = useRef<StripePaymentInputRef>(null);

    const { isSubmitting, termsAgreed, setTermsAgreed, trialDays, handleSubmit, duplicatePaymentCheck } =
        useStartTrialModal({ isOpen, onClose });

    const gdprDocument = getGDPRComplianceDocument(country);

    const handleFormSubmit = () => {
        handleSubmit(stripeRef);
    };

    return (
        <>
            <StanModal
                id='start-trial-modal'
                isOpen={isOpen}
                onClose={onClose}
                title='Save $100s with Stan as your All-In-One Store'
                size='sm'
            >
                <div className='-mt-2 flex flex-col gap-4'>
                    {/* Trial Disclaimer */}
                    <div className='bg-brand-dimmed flex flex-col gap-2 rounded-2xl p-5'>
                        <p className='text-dark text-body-sm font-bold'>
                            You won&apos;t be charged for your free trial!
                        </p>
                        <p className='text-body-xs text-neutral-fg-primary-moderate'>
                            We ask for your credit card information for a seamless subscription experience, but you can
                            cancel anytime and we&apos;ll send you a heads-up email in advance.
                        </p>
                    </div>

                    {/* Payment Input */}
                    <div>
                        <StripePaymentInput ref={stripeRef} />
                    </div>

                    {/* Terms Checkbox */}
                    <div className='flex items-start gap-2'>
                        <StanCheckbox
                            id='trial-terms-checkbox'
                            checked={termsAgreed}
                            onChange={e => setTermsAgreed(e.target.checked)}
                        />
                        <label
                            htmlFor='trial-terms-checkbox'
                            className='text-body-xs text-dark cursor-pointer font-medium'
                        >
                            I acknowledge that I have read and understand the{' '}
                            <a
                                href={LEGAL_URLS.TERMS_OF_SERVICE}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-brand-main underline focus-visible:ring-2'
                            >
                                Terms of Service
                            </a>{' '}
                            and the{' '}
                            <a
                                href={LEGAL_URLS.PRIVACY_POLICY}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-brand-main underline focus-visible:ring-2'
                            >
                                Privacy Policy
                            </a>
                            {gdprDocument && (
                                <>
                                    {'. '}Our{' '}
                                    <a
                                        href={gdprDocument}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-brand-main underline focus-visible:ring-2'
                                    >
                                        GDPR compliance certificate
                                    </a>{' '}
                                    is available for review.
                                </>
                            )}
                        </label>
                    </div>

                    {/* Submit Button */}
                    <StanButton
                        name='subscribe-button'
                        type='button'
                        color='primary'
                        size='lg'
                        loading={isSubmitting}
                        disabled={!termsAgreed}
                        onClick={handleFormSubmit}
                        className='w-full'
                    >
                        Start Your {trialDays} Day Free Trial
                    </StanButton>
                </div>
            </StanModal>

            {/* Duplicate Payment Method Modal */}
            <DuplicatePaymentModal
                isOpen={duplicatePaymentCheck.isModalOpen}
                maskedEmail={duplicatePaymentCheck.maskedEmail}
                trialDays={trialDays}
                onConfirm={duplicatePaymentCheck.handleConfirm}
                onGoToLogin={duplicatePaymentCheck.handleGoToLogin}
            />
        </>
    );
};

export default StartTrialModal;
